/**
 * Edit Assignment Dialog
 *
 * Dialog for creating and editing course assignments.
 * Handles both create and edit modes based on whether assignment prop is provided.
 */

import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Typography,
} from '../ui';
import { assignmentDefinitionsClient, coursesClient } from '../../apis/client-instances';
import { AssignmentDefinition, Course, UpdateAssignmentDefinitionRequest } from '../../apis/api-client';
import { alert } from '../../hooks/function';
import { CourseAutocomplete } from '../common';
import TargetSelector from './TargetSelector';

interface EditAssignmentDialogProps {
  open: boolean;
  courseId?: number | null;
  /** Bulk mode: when provided, the same form payload is sent to each course
   *  via sequential createDefinition calls. Hides the course picker and the
   *  edit/update path. Mutually exclusive with `assignment`. */
  courseIds?: number[];
  assignment?: AssignmentDefinition | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EditAssignmentDialog({
  open,
  courseId,
  courseIds,
  assignment,
  onClose,
  onSuccess,
}: EditAssignmentDialogProps) {
  const isBulk = !!courseIds && courseIds.length > 0;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  // EDIT mode: single course chip (existing assignments are 1:1 with a
  // course). CREATE mode: multi-select array so admins can stamp the
  // same dates + targets across several courses or learning paths in
  // one go. Bulk mode (parent-provided `courseIds`) hides the picker
  // entirely so neither field is used.
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedCourses, setSelectedCourses] = useState<Course[]>([]);
  const [formData, setFormData] = useState({
    courseId: 0,
    courseIds: [] as number[],
    startDate: '',
    deadline: '',
    targetUserIds: [] as number[],
    targetTagIds: [] as number[],
    targetGroupIds: [] as number[],
    targetProfessionIds: [] as number[],
  });
  const [errors, setErrors] = useState<any>({});
  // Onboarding-only flag. When true, the assignment skips current
  // matchers and only enrolls learners who join after creation.
  // Persisted on `AssignmentDefinition.isOnboardingOnly`.
  const [onboardingOnly, setOnboardingOnlyState] = useState(false);

  const isEditMode = !!assignment;

  // Initialize form data when assignment changes or dialog opens
  useEffect(() => {
    if (open) {
      if (assignment) {
        // Edit mode: populate with assignment data
        setFormData({
          courseId: assignment.courseId || 0,
          courseIds: [],
          startDate: assignment.startDate ? formatDateForInput(assignment.startDate) : '',
          deadline: assignment.deadline ? formatDateForInput(assignment.deadline) : '',
          targetUserIds: assignment.targetUserIds || [],
          targetTagIds: assignment.targetTagIds || [],
          targetGroupIds: assignment.targetGroupIds || [],
          targetProfessionIds: assignment.targetProfessionIds || [],
        });
        setOnboardingOnlyState(assignment.isOnboardingOnly ?? false);
      } else {
        // Create mode: clear form. When the parent pinned a courseId
        // (single-course surface inside a course's edit page), seed
        // it as the only selection so the picker still reads "this
        // course" without the admin having to retype it.
        setFormData({
          courseId: courseId || 0,
          courseIds: courseId ? [courseId] : [],
          startDate: '',
          deadline: '',
          targetUserIds: [],
          targetTagIds: [],
          targetGroupIds: [],
          targetProfessionIds: [],
        });
        setSelectedCourses([]);
        setOnboardingOnlyState(false);
      }
      setErrors({});
    }
  }, [open, assignment, courseId]);

  // Hydrate the course chip from formData.courseId (covers both edit mode and
  // re-opens). Skipped when the parent already pinned a courseId (single-course
  // view doesn't show the picker).
  useEffect(() => {
    if (courseId || !formData.courseId) {
      setSelectedCourse(null);
      return;
    }
    if (selectedCourse?.id === formData.courseId) return;
    let cancelled = false;
    coursesClient
      .getCourseById(formData.courseId)
      .then((c) => { if (!cancelled) setSelectedCourse(c ?? null); })
      .catch(() => { if (!cancelled) setSelectedCourse(null); });
    return () => { cancelled = true; };
  }, [formData.courseId, courseId]);

  // Format Date object to date input format (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const handleChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleTargetChange = (
    tagIds: number[],
    groupIds: number[],
    professionIds: number[],
    userIds: number[],
  ) => {
    // Individual users are back as a first-class targeting facet on
    // the bulk dialog — the Users tab in TargetSelector surfaces them
    // alongside tags/groups/roles. Existing edit-mode assignments
    // still round-trip their saved user ids cleanly through this same
    // path.
    setFormData({
      ...formData,
      targetTagIds: tagIds,
      targetGroupIds: groupIds,
      targetProfessionIds: professionIds,
      targetUserIds: userIds,
    });
    setErrors({ ...errors, target: '' });
  };

  const validate = () => {
    const newErrors: any = {};

    // Bulk mode skips the course requirement — courseIds are provided up
    // front. Edit mode uses the single courseId. Create mode requires
    // at least one selection in the multi-select list.
    if (!isBulk) {
      if (isEditMode) {
        if (!formData.courseId) newErrors.courseId = 'Course is required';
      } else if (!courseId && formData.courseIds.length === 0) {
        newErrors.courseId = 'Select at least one course, learning path, or event';
      }
    }

    if (!formData.deadline) {
      newErrors.deadline = 'Deadline is required';
    }

    if (formData.startDate && formData.deadline) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.deadline);
      if (start >= end) {
        newErrors.startDate = 'Start date must be before deadline';
      }
    }

    if (
      formData.targetUserIds.length === 0 &&
      formData.targetTagIds.length === 0 &&
      formData.targetGroupIds.length === 0 &&
      formData.targetProfessionIds.length === 0
    ) {
      newErrors.target = 'Select at least one user, tag, group, or profession';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      if (isEditMode && assignment?.id) {
        // Update existing assignment
        const request: UpdateAssignmentDefinitionRequest = {
          startDate: formData.startDate ? new Date(formData.startDate) : undefined,
          deadline: new Date(formData.deadline),
          targetUserIds: formData.targetUserIds.length > 0 ? formData.targetUserIds : undefined,
          targetTagIds: formData.targetTagIds.length > 0 ? formData.targetTagIds : undefined,
          targetGroupIds: formData.targetGroupIds.length > 0 ? formData.targetGroupIds : undefined,
          targetProfessionIds:
            formData.targetProfessionIds.length > 0 ? formData.targetProfessionIds : undefined,
          isOnboardingOnly: onboardingOnly,
        };

        await assignmentDefinitionsClient.updateDefinition(assignment.id, request);
        alert(enqueueSnackbar, 'Assignment updated successfully!', 'success');
      } else if (isBulk) {
        // Bulk mode: same payload, fired once per selected courseId.
        // Sequential rather than Promise.all so we can report partial
        // failures cleanly (and avoid hammering the API).
        const baseRequest = {
          startDate: formData.startDate ? new Date(formData.startDate) : undefined,
          deadline: new Date(formData.deadline),
          targetUserIds: formData.targetUserIds.length > 0 ? formData.targetUserIds : undefined,
          targetTagIds: formData.targetTagIds.length > 0 ? formData.targetTagIds : undefined,
          targetGroupIds: formData.targetGroupIds.length > 0 ? formData.targetGroupIds : undefined,
          targetProfessionIds:
            formData.targetProfessionIds.length > 0 ? formData.targetProfessionIds : undefined,
          isOnboardingOnly: onboardingOnly,
        };
        let ok = 0;
        let fail = 0;
        for (const cid of courseIds!) {
          try {
            await assignmentDefinitionsClient.createDefinition({ ...baseRequest, courseId: cid } as AssignmentDefinition);
            ok += 1;
          } catch {
            fail += 1;
          }
        }
        if (fail === 0) {
          alert(enqueueSnackbar, `Created ${ok} ${ok === 1 ? 'assignment' : 'assignments'}`, 'success');
        } else {
          alert(
            enqueueSnackbar,
            `Created ${ok}; failed ${fail}`,
            ok > 0 ? 'warning' : 'error'
          );
        }
      } else {
        // Create — multi-select. The multi-content selector lets admins
        // stamp the same dates + targets across several courses or
        // learning paths in one go, so we fan out createDefinition once
        // per selected id (same sequential pattern as the bulk branch
        // above; lets us report partial failures cleanly).
        const ids = courseId ? [courseId] : formData.courseIds;
        const baseRequest = {
          startDate: formData.startDate ? new Date(formData.startDate) : undefined,
          deadline: new Date(formData.deadline),
          targetUserIds: formData.targetUserIds.length > 0 ? formData.targetUserIds : undefined,
          targetTagIds: formData.targetTagIds.length > 0 ? formData.targetTagIds : undefined,
          targetGroupIds: formData.targetGroupIds.length > 0 ? formData.targetGroupIds : undefined,
          targetProfessionIds:
            formData.targetProfessionIds.length > 0 ? formData.targetProfessionIds : undefined,
          isOnboardingOnly: onboardingOnly,
        };
        let ok = 0;
        let fail = 0;
        for (const cid of ids) {
          try {
            await assignmentDefinitionsClient.createDefinition({
              ...baseRequest,
              courseId: cid,
            } as AssignmentDefinition);
            ok += 1;
          } catch {
            fail += 1;
          }
        }
        if (fail === 0) {
          alert(
            enqueueSnackbar,
            ok === 1
              ? 'Assignment created successfully!'
              : `Created ${ok} assignments`,
            'success',
          );
        } else {
          alert(
            enqueueSnackbar,
            `Created ${ok}; failed ${fail}`,
            ok > 0 ? 'warning' : 'error',
          );
        }
      }

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      alert(
        enqueueSnackbar,
        error?.response?.data || `Failed to ${isEditMode ? 'update' : 'create'} assignment`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      courseId: courseId || 0,
      courseIds: [],
      startDate: '',
      deadline: '',
      targetUserIds: [],
      targetTagIds: [],
      targetGroupIds: [],
      targetProfessionIds: [],
    });
    setSelectedCourses([]);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isBulk
          ? `Assign ${courseIds!.length} ${courseIds!.length === 1 ? 'course' : 'courses'}`
          : isEditMode
            ? 'Edit Assignment'
            : selectedCourses.length > 1
              ? `Create ${selectedCourses.length} Assignments`
              : 'Create Assignment'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!courseId && !isEditMode && !isBulk && (
            // Create mode — multi-select picker over courses, learning
            // paths, and events. The same dates + targets stamp across
            // every selected item (one createDefinition call per id,
            // sequential so partial failures report cleanly).
            <CourseAutocomplete
              multiple
              value={selectedCourses}
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : v ? [v as Course] : [];
                setSelectedCourses(arr);
                setFormData((cur) => ({
                  ...cur,
                  courseIds: arr.map((c) => c.id!).filter(Boolean),
                }));
                setErrors((cur: any) => ({ ...cur, courseId: '' }));
              }}
              size="medium"
              fullWidth
              label="Courses, paths, or events"
              placeholder="Search and pick one or more"
              required
              error={!!errors.courseId}
              helperText={
                errors.courseId ||
                (selectedCourses.length > 1
                  ? `${selectedCourses.length} items selected — the dates and targets below will apply to each.`
                  : 'Search courses, learning paths, and events. Pick one or several to assign together.')
              }
            />
          )}

          <TextField
            label="Eligibility Start Date"
            type="date"
            fullWidth
            value={formData.startDate}
            onChange={(e) => handleChange('startDate', e.target.value)}
            error={!!errors.startDate}
            helperText={errors.startDate || 'Optional: When users can start the course'}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Deadline"
            type="date"
            fullWidth
            value={formData.deadline}
            onChange={(e) => handleChange('deadline', e.target.value)}
            error={!!errors.deadline}
            helperText={errors.deadline || 'Required: When the course must be completed'}
            required
            InputLabelProps={{ shrink: true }}
          />

          <TargetSelector
            selectedTagIds={formData.targetTagIds}
            selectedGroupIds={formData.targetGroupIds}
            selectedProfessionIds={formData.targetProfessionIds}
            selectedUserIds={formData.targetUserIds}
            onChange={handleTargetChange}
            error={errors.target}
          />

          {/* Onboarding-only toggle. Label is intentionally temporal
              ("after today") so the rule's forever-forward nature is
              clear on first read AND on the next admin's return six
              months later — avoids the "is this a one-time filter or
              a durable rule?" ambiguity a vaguer label invites. */}
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: onboardingOnly ? 'primary.main' : 'divider',
              bgcolor: onboardingOnly ? 'rgba(var(--brand-rgb), 0.08)' : 'grey.50',
              transition: 'border-color 150ms ease, background-color 150ms ease',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={onboardingOnly}
                  onChange={(e) => setOnboardingOnlyState(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Only give this to learners who join after today
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Existing matching learners won't be enrolled. Useful for new-employee onboarding.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0, '& .MuiCheckbox-root': { pt: 0.25 } }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : isEditMode ? (
            'Update Assignment'
          ) : selectedCourses.length > 1 ? (
            `Create ${selectedCourses.length} Assignments`
          ) : (
            'Create Assignment'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
